import {Component, Inject, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {PersonalService} from '../personal/personal.service';
import {Employee} from '../admin/models/employee.model';
import {Vacation} from '../admin/models/vacation.model';
import {ManagerService} from '../manager/manager.service';
import {AuthenticationService} from '../core/authentication/authentication.service';
import {FabMessageService} from '@fabric/components/message';

@Component({
    selector: 'app-absence-calendar',
    templateUrl: './absence-calendar-pop-up.component.html',
    styleUrls: ['./absence-calendar-pop-up.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AbsenceCalendarPopUpComponent implements OnInit {

    public employeeList: Employee[] = [];
    public vacationList: Vacation[] = [];
    public dateEmployees: any[] = [];
    public selected: any;
    public daysSelected: any[] = [];
    public today = new Date();
    public todayString = '';
    public negative2 = -2;
    public gminValue: string = '';
    public getTeamID = new Map<string, string>();
    public isLoading = false;
    public isSelected = (event: any) => {
        const date = event.getFullYear() + '-' +
            ('00' + (event.getMonth() + 1)).slice(this.negative2) + '-' +
            ('00' + event.getDate()).slice(this.negative2);
        return this.daysSelected.find(x => x === date) ? 'selected' : '';
    };

    public fieldNames = ['firstName', 'lastName', 'gmin', 'startDate', 'endDate', 'absenceType',
        'numberOfAnnualLeaveDays', 'requestStatus'];
    public displayNames = ['First Name', 'Last Name', 'GMIN', 'Start Date', 'End Date', 'Absence Type',
        'Absence Length', 'Request Status'];

    @ViewChild('calendar') calendar: any;

    constructor(public dialogRef: MatDialogRef<[]>,
                private readonly personalService: PersonalService,
                private readonly  managerService: ManagerService,
                private authService: AuthenticationService,
                private fabMessageService: FabMessageService,
                @Inject(MAT_DIALOG_DATA) public selectedData: any) {
    }

    ngOnInit(): void {
        this.isLoading = true;
        if (this.selectedData[1] == 'manager') {
            this.gminValue = this.authService.userInfo.gmin || '';
            this.managerService.fetchTeamIdByGmin(this.gminValue).subscribe({
                next: (data) => {
                    if (data.length > 0) {
                        for (const team of data) {
                            this.managerService.fetchAllEmployeeDetailsByTeamId(team.id).subscribe({
                                next: (value) => {
                                    if (value.length > 0) {
                                        this.employeeList.push(...value);
                                    }
                                }, error: () => this.fabMessageService.displayErrorMessage('Error in fetching team ID')
                            });
                        }
                        this.isLoading = false;
                    }
                    else {
                        this.isLoading = false;
                        this.fabMessageService.displayErrorMessage('No associated Teams');
                    }
                }, error: () => {
                    this.isLoading = false;
                    this.fabMessageService.displayErrorMessage('Error fetching teams using GMIN');
                }, complete: () => {
                    setTimeout(() => {
                        this.getVacationList();
                        this.getListToday();
                        this.displayVacationApprovedDates();
                        this.calendar.updateTodaysDate();
                        this.isLoading = true;
                    }, 3000);
                }
            });
        }
        else {
            this.employeeList = this.selectedData[0];
            this.getVacationList();
            this.getListToday();
            this.displayVacationApprovedDates();
        }
        this.todayString = this.today.toLocaleDateString('en-gb');
    }

    /**
     * Gets a list of vacations from the employee objects
     */
    public getVacationList(): void {
        this.employeeList.forEach((employee) => {
            employee.vacation.forEach((request) => {
                this.vacationList.push(request);
            });
        });
    }

    /**
     * Changes the selected date and calls getEmployeesOnDate
     * @param event the date to be changed to
     * @param calendar the calendar to update
     */
    public select(event: any, calendar: any): void {
        let date = event.getFullYear() + '-' +
            ('00' + (event.getMonth() + 1)).slice(this.negative2) + '-' +
            ('00' + event.getDate()).slice(this.negative2);
        date = this.personalService.convertDateFormatForAPI(date);
        this.selected = date;
        calendar.updateTodaysDate();
        this.getEmployeesOnDate();
    }

    /**
     * This gets an array of vacations for the selected date, loops through the employee list to get
     * the employee with the vacation, and pushes this data to the dateEmployees object
     */
    public getEmployeesOnDate(): void {
        const arr = this.vacationList.filter((vacation) => {
            const start = new Date(this.personalService.swapDateAndMonth(vacation.startDate)).getTime();
            const end = new Date(this.personalService.swapDateAndMonth(vacation.endDate)).getTime();
            const dateSelected = new Date(this.personalService.swapDateAndMonth(this.selected)).getTime();
            if (start <= dateSelected && dateSelected <= end && vacation.requestStatus !== 'DELETED') {
                return vacation;
            }
        });
        const employeeArr: any[] = [];
        this.employeeList.forEach((employee) => {
            arr.forEach((vacation) => {
                const employeeVac: any = {
                    employeeId: employee.id,
                    firstName: employee.firstName,
                    lastName: employee.lastName,
                    gmin: employee.gmin,
                    startDate: vacation.startDate,
                    endDate: vacation.endDate,
                    absenceType: vacation.absenceType,
                    numberOfAnnualLeaveDays: vacation.numberOfAnnualLeaveDays,
                    requestStatus: vacation.requestStatus
                };
                if (vacation.employeeId === employee.id && !employeeArr.includes(employeeVac)) {
                    employeeArr.push(employeeVac);
                }
            });
        });
        this.dateEmployees = employeeArr;
    }

    /**
     * This gets an array of vacations for today's date, loops through the employee list to get
     * the employee with the vacation, and pushes this data to the dateEmployees object
     */
    public getListToday() {
        const arr = this.vacationList.filter((vacation) => {
            const start = new Date(this.personalService.swapDateAndMonth(vacation.startDate)).getTime();
            const end = new Date(this.personalService.swapDateAndMonth(vacation.endDate)).getTime();
            const dateSelected = new Date(this.personalService.swapDateAndMonth(this.todayString)).getTime();
            if (start <= dateSelected && dateSelected <= end && vacation.requestStatus !== 'DELETED') {
                return vacation;
            }
        });
        const employeeArr: any[] = [];
        this.employeeList.forEach((employee) => {
            arr.forEach((vacation) => {
                const employeeVac: any = {
                    employeeId: employee.id,
                    firstName: employee.firstName,
                    lastName: employee.lastName,
                    gmin: employee.gmin,
                    startDate: vacation.startDate,
                    endDate: vacation.endDate,
                    absenceType: vacation.absenceType,
                    numberOfAnnualLeaveDays: vacation.numberOfAnnualLeaveDays,
                    requestStatus: vacation.requestStatus
                };
                if (vacation.employeeId === employee.id && !employeeArr.includes(employeeVac)) {
                    employeeArr.push(employeeVac);
                }
            });
        });
        this.dateEmployees = employeeArr;
    }

    /** This adds the list of vacation start dates to the daysSelected array so that those
     * dates are selected on the calendar
     */
    public displayVacationApprovedDates(): void {
        for (let vacation in this.vacationList) {
            if(this.vacationList[vacation].requestStatus == 'APPROVED') {
                let startDateArray = this.vacationList[vacation].startDate.split('/')
                let startDate = startDateArray[2] + "-" + startDateArray[1] + "-" + startDateArray[0];

                let endDateArray = this.vacationList[vacation].endDate.split('/')
                let endDate = endDateArray[2] + "-" + endDateArray[1] + "-" + endDateArray[0];

                if(!this.daysSelected.includes(startDate) && !this.daysSelected.includes(endDate)) {
                    this.daysSelected.push(startDate);
                    this.daysSelected.push(endDate);
                }
            }
        }
    }
    /**
     * Closes the popup
     */
    close(): void {
        this.dialogRef.close();
    }
}
